import { apiService } from '@/services/ApiService.ts';
import { useSessionStore } from '@/stores/SessionStore.ts';
import type { AiModel } from '@/types/AiModel.ts';
import { logger } from '@/lib/logger.ts';
import { usePromptStore } from '@/stores/PromptStore.ts';
import { createTextToast } from '@/utils/ToastUtils.ts';
import { DEFAULT_AI_MODEL } from '@/constants/DefaultAiModel.ts';
import { normalizeReasoningEffort, resolveClientModelId } from '@/utils/ModelCompatibilityUtils.ts';
import { REASONING_EFFORTS, type ReasoningEffort } from '@/types/ReasoningEffort.ts';

export const fetchAiModelsAction = async () => {
    const sessionStore = useSessionStore();
    sessionStore.aiModelCatalogStatus = 'loading';

    let apiResponse;
    try {
        apiResponse = await apiService.getApi().openrouterModelsV1();
    } catch (error) {
        logger.error('Failed to fetch AI models:', error);
        sessionStore.aiModelCatalogStatus = 'failed';
        createTextToast(
            'Model catalog unavailable',
            'Your profiles are unchanged. Starting a run will retry automatically.',
            'destructive',
            false,
        );

        return;
    }

    const aiModels: Record<AiModel['id'], AiModel> = {};

    const availableModelIds = new Set([
        ...apiResponse.textTextModelIds,
        ...apiResponse.textImageModelIds,
    ]);
    const knownReasoningEfforts = new Set<string>(REASONING_EFFORTS);
    for (const m of apiResponse.models) {
        const model = m as {
            id: string;
            name: string;
            reasoning?: {
                supported_efforts?: string[];
                default_effort?: string | null;
            } | null;
        };
        if (!availableModelIds.has(model.id)) continue;

        const publishedReasoningEfforts = new Set(model.reasoning?.supported_efforts ?? []);
        const supportedReasoningEfforts = REASONING_EFFORTS.filter(
            (effort): effort is ReasoningEffort => publishedReasoningEfforts.has(effort),
        );
        const defaultReasoningEffort = model.reasoning?.default_effort;

        aiModels[model.id] = {
            id: model.id,
            name: model.name,
            isRecommended: apiResponse.recommended.includes(model.id),
            supportsStructuredOutput: apiResponse.structuredOutputModelIds.includes(model.id),
            supportsImage: apiResponse.textImageModelIds.includes(model.id),
            supportedReasoningEfforts,
            defaultReasoningEffort:
                defaultReasoningEffort &&
                knownReasoningEfforts.has(defaultReasoningEffort) &&
                supportedReasoningEfforts.includes(defaultReasoningEffort as ReasoningEffort)
                    ? (defaultReasoningEffort as ReasoningEffort)
                    : undefined,
        };
    }

    const defaultModelId = apiResponse.defaultModelId || DEFAULT_AI_MODEL;
    const modelReplacements = (apiResponse.modelReplacements ?? {}) as Record<string, string>;
    sessionStore.$patch({
        aiModels,
        aiModelCatalogStatus: 'ready',
        defaultModelId,
        modelReplacements,
    });

    const promptStore = usePromptStore();
    const migratedPrompts = Object.fromEntries(
        Object.entries(promptStore.promptConfigs).map(([id, prompt]) => {
            const resolvedModelId = resolveClientModelId(
                prompt,
                aiModels,
                defaultModelId,
                modelReplacements,
            );

            if (!resolvedModelId) return [id, prompt];

            const reasoningEffort = normalizeReasoningEffort(
                aiModels[resolvedModelId],
                prompt.reasoningEffort,
            );

            return [
                id,
                resolvedModelId !== prompt.modelId || reasoningEffort !== prompt.reasoningEffort
                    ? { ...prompt, modelId: resolvedModelId, reasoningEffort }
                    : prompt,
            ];
        }),
    );
    const migratedCount = Object.values(migratedPrompts).filter((prompt) => {
        const storedPrompt = promptStore.promptConfigs[prompt.id];
        return (
            prompt.modelId !== storedPrompt?.modelId ||
            prompt.reasoningEffort !== storedPrompt?.reasoningEffort
        );
    }).length;

    if (migratedCount > 0) {
        try {
            localStorage.setItem(
                'prompt-model-backup-latest',
                JSON.stringify({
                    profileConfigsString: promptStore.profileConfigsString,
                    stepConfigsString: promptStore.stepConfigsString,
                    promptConfigsString: promptStore.promptConfigsString,
                }),
            );
        } catch (error) {
            logger.warn('Failed to back up prompts before model migration:', error);
        }
        promptStore.setPromptConfigs(migratedPrompts);
        createTextToast(
            'Profiles updated',
            `${migratedCount} prompt setting${migratedCount === 1 ? '' : 's'} updated to match current model capabilities.`,
            'default',
            false,
        );
    }

    return apiResponse;
};
